import { useCallback, useSyncExternalStore } from "react";

export type AccentColor =
  | "amber"
  | "blue"
  | "green"
  | "purple"
  | "rose"
  | "teal";

const STORAGE_KEY = "palnia_accent";
const VALID_ACCENTS: readonly AccentColor[] = [
  "amber",
  "blue",
  "green",
  "purple",
  "rose",
  "teal",
] as const;

const ACCENT_COLORS: Record<
  AccentColor,
  { primary: string; hover: string; darkPrimary: string; darkHover: string }
> = {
  amber: {
    primary: "#d97706",
    hover: "#b45309",
    darkPrimary: "#f59e0b",
    darkHover: "#fbbf24",
  },
  blue: {
    primary: "#2563eb",
    hover: "#1d4ed8",
    darkPrimary: "#3b82f6",
    darkHover: "#60a5fa",
  },
  green: {
    primary: "#16a34a",
    hover: "#15803d",
    darkPrimary: "#22c55e",
    darkHover: "#4ade80",
  },
  purple: {
    primary: "#9333ea",
    hover: "#7e22ce",
    darkPrimary: "#a855f7",
    darkHover: "#c084fc",
  },
  rose: {
    primary: "#e11d48",
    hover: "#be123c",
    darkPrimary: "#f43f5e",
    darkHover: "#fb7185",
  },
  teal: {
    primary: "#0d9488",
    hover: "#0f766e",
    darkPrimary: "#14b8a6",
    darkHover: "#2dd4bf",
  },
};

interface AccentStore {
  accent: AccentColor;
}

function loadAccent(): AccentColor {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && (VALID_ACCENTS as readonly string[]).includes(stored)) {
    return stored as AccentColor;
  }
  return "amber";
}

const accentStore: AccentStore = {
  accent: loadAccent(),
};

const listeners = new Set<() => void>();

function notifyListeners() {
  for (const listener of listeners) { try { listener(); } catch { /* ignore stale listener during teardown */ } }
}

function applyAccent() {
  if (typeof window === "undefined") return;
  const colors = ACCENT_COLORS[accentStore.accent];
  const isDark = document.documentElement.classList.contains("dark");

  document.documentElement.style.setProperty(
    "--color-accent",
    isDark ? colors.darkPrimary : colors.primary,
  );
  document.documentElement.style.setProperty(
    "--color-accent-hover",
    isDark ? colors.darkHover : colors.hover,
  );
}

function setAccent(newAccent: AccentColor) {
  accentStore.accent = newAccent;
  localStorage.setItem(STORAGE_KEY, newAccent);
  applyAccent();
  notifyListeners();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): AccentColor {
  return accentStore.accent;
}

applyAccent();

export function useAccent() {
  const accent = useSyncExternalStore(subscribe, getSnapshot);

  return {
    accent,
    setAccent: useCallback(
      (newAccent: AccentColor) => setAccent(newAccent),
      [],
    ),
  };
}
